import { useEffect, useState } from "react";
import { Bank, ArrowSquareOut, Percent, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const StripeConnectCard = () => {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState("");

  const load = () => api.get("/connect/coach/status").then(({ data }) => { setStatus(data); setPct(String(data.commission_percent)); }).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("stripe") === "connected") {
      window.history.replaceState({}, "", window.location.pathname);
      toast.success("Stripe account linked — payments now go straight to you");
      load(); refresh();
    }
  }, [refresh]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connect/coach/onboarding-link", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start Stripe onboarding"); setBusy(false); }
  };
  const simulate = async () => {
    setBusy(true);
    try { await api.post("/connect/coach/simulate-active"); toast.success("Stripe account simulated as active"); await load(); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const saveCommission = async () => {
    try {
      const { data } = await api.patch("/platform/commission", { commission_percent: Number(pct) });
      toast.success(`Traina commission set to ${data.commission_percent}%`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  if (!status) return null;
  const ready = status.payments_ready;
  return (
    <section className={`card-flat p-6 ${ready ? "" : "border-amber-500/40"}`} data-testid="stripe-connect-card">
      <div className="flex items-center gap-3 mb-4">
        <Bank size={20} weight="duotone" className={ready ? "text-emerald-400" : "text-amber-400"} />
        <div>
          <h2 className="font-display text-xl">Payments</h2>
          <p className="tracker text-[10px] text-zinc-500">Every client payment runs through Stripe · Traina keeps {status.commission_percent}% per transaction</p>
        </div>
      </div>

      {status.collects_directly ? (
        <div className="space-y-2">
          <p className="text-sm text-zinc-300 flex items-center gap-2" data-testid="stripe-connect-owner"><CheckCircle size={16} weight="fill" className="text-emerald-400" /> Platform owner — payments land directly in the Traina Stripe account, no commission taken.</p>
          <p className="tracker text-[9px] text-zinc-500" data-testid="stripe-connect-mode">
            Coach onboarding: {status.mode === "stub" ? <span className="text-amber-400">SIMULATE — add STRIPE_CONNECT_SECRET_KEY to enable real Stripe Connect</span> : <span className="text-emerald-400">{status.mode.toUpperCase()} · Stripe Connect active ({status.country})</span>}
          </p>
        </div>
      ) : ready ? (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-zinc-300 flex items-center gap-2" data-testid="stripe-connect-active"><CheckCircle size={16} weight="fill" className="text-emerald-400" /> Stripe account linked — bookings, packs and invoices pay out to you{status.payouts_enabled ? "" : " (payouts pending Stripe review)"}.</p>
          {!status.stub && <button onClick={connect} disabled={busy} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid="stripe-connect-manage"><ArrowSquareOut size={12} /> Update details</button>}
        </div>
      ) : (
        <div className="space-y-3" data-testid="stripe-connect-pending">
          <p className="text-sm text-zinc-300 flex items-start gap-2"><Warning size={16} weight="fill" className="text-amber-400 shrink-0 mt-0.5" /> Link your Stripe account to publish classes and take payments. {status.stripe_status === "pending" || status.stripe_status === "incomplete" ? "Onboarding started — finish the remaining steps." : "Takes about 5 minutes; payouts go straight to your bank."}</p>
          <div className="flex gap-2 flex-wrap">
            <button onClick={connect} disabled={busy} className="btn-primary !py-2 !px-4 !text-xs" data-testid="stripe-connect-start">
              <Bank size={14} /> {busy ? "Opening Stripe…" : status.stripe_account_id ? "Continue Stripe setup" : "Link Stripe account"}
            </button>
            {status.mode === "stub" && (
              <button onClick={simulate} disabled={busy} className="btn-ghost !py-2 !px-4 !text-xs" title="Preview only — Connect keys not configured" data-testid="stripe-connect-simulate">Simulate linked (preview)</button>
            )}
          </div>
        </div>
      )}

      {user?.is_owner && (
        <div className="mt-5 pt-5 border-t border-white/10 flex items-end gap-3 flex-wrap" data-testid="commission-editor">
          <div>
            <label className="tracker text-zinc-500 text-[10px] block mb-2">Traina commission on coach payments</label>
            <div className="flex items-center gap-2">
              <input type="number" min="0" max="50" step="0.5" value={pct} onChange={(e) => setPct(e.target.value)} className="w-24 bg-transparent border border-white/10 px-3 py-2 text-sm outline-none" data-testid="commission-input" />
              <Percent size={14} className="text-zinc-500" />
            </div>
          </div>
          <button onClick={saveCommission} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="commission-save">Save</button>
          <p className="tracker text-[9px] text-zinc-600 basis-full">Applied as a Stripe application fee on every payment routed to a connected coach account. Your own business pays nothing.</p>
        </div>
      )}
    </section>
  );
};
